import { FC, useCallback, useState } from "react";

import { HStack, RangeSlider, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { DemographicDistribution } from "@/types/resources";

type DraftCustomRange = { max: number | null; min: number | null };
type DraftDemographicDistribution = (
  | DemographicDistribution<"age">
  | {
      label: null;
      value: DraftCustomRange;
      weight: number;
    }
) & {
  _key: string;
};

interface AgeRangeEditorProps {
  disabled?: boolean;
  distribution: DemographicDistribution<"age">[];
  onChange: (distribution: DemographicDistribution<"age">[]) => void;
}

const MIN_AGE = 8;
const MAX_AGE = 100;
const MAX_RANGES = 5;

// const distributeWeightEqually = (
//   distribution: DemographicDistribution<"age">[]
// ): DemographicDistribution<"age">[] => {
//   if (distribution.length === 0) return distribution;
//
//   const totalWeight = distribution.reduce(
//     (sum, item) => sum + (item.weight || 0),
//     0
//   );
//
//   // Only distribute equally if all weights are 0 (initial state)
//   if (totalWeight === 0) {
//     const equalWeight = 1 / distribution.length;
//     return distribution.map((item) => ({ ...item, weight: equalWeight }));
//   }
//
//   // Don't normalize - allow partial distributions
//   return distribution;
// };

const isValidRange = (value: {
  max: number | null;
  min: number | null;
}): boolean => {
  return (
    value.min !== null &&
    value.max !== null &&
    value.min < value.max &&
    value.min >= MIN_AGE &&
    value.max <= MAX_AGE
  );
};

// Build local drafts from the prop, appending an empty row. Uses stable
// index-based keys to prevent focus loss when typing.
const buildDrafts = (
  distribution: DemographicDistribution<"age">[]
): DraftDemographicDistribution[] => {
  const newDrafts: DraftDemographicDistribution[] = distribution.map(
    (dist, index) => ({
      ...dist,
      _key: `age-${index}`,
    })
  );
  if (newDrafts.length < MAX_RANGES) {
    newDrafts.push({
      _key: `age-${newDrafts.length}`,
      label: null,
      value: { max: null, min: null },
      weight: newDrafts.length === 0 ? 1 : 0,
    });
  }
  return newDrafts;
};

const AgeRangeEditor: FC<AgeRangeEditorProps> = ({
  disabled = false,
  distribution,
  onChange,
}) => {
  const { i18n } = useLocalization();
  const [drafts, setDrafts] = useState<DraftDemographicDistribution[]>(() =>
    buildDrafts(distribution)
  );

  // Sync from prop to local drafts during render (not in an effect) whenever
  // the distribution prop changes.
  const [prevDistribution, setPrevDistribution] = useState(distribution);
  if (prevDistribution !== distribution) {
    setPrevDistribution(distribution);
    setDrafts(buildDrafts(distribution));
  }

  // const updateWeight = useCallback(
  //   (index: number, newWeight: number) => {
  //     const newDrafts = drafts.map((item, i) =>
  //       i === index ? { ...item, weight: newWeight / 100 } : item
  //     );
  //     onChange(
  //       newDrafts.filter(
  //         (d) => d.value.min !== null && d.value.max !== null
  //       ) as DemographicDistribution<"age">[]
  //     );
  //   },
  //   [drafts, onChange]
  // );
  //
  // const removeRange = useCallback(
  //   (index: number) => {
  //     const newDrafts = drafts.filter((_, i) => i !== index);
  //     const valid = newDrafts
  //       .filter((d) => isValidRange(d.value))
  //       .map(({ _key, ...rest }) => rest as DemographicDistribution<"age">);
  //     onChange(distributeWeightEqually(valid));
  //   },
  //   [drafts, onChange]
  // );

  const updateAgeRange = useCallback(
    (index: number, field: "min" | "max", value: string) => {
      const num = parseInt(value);

      const newDrafts = drafts.map((item, i) => {
        if (i !== index) return item;

        const newValue = isNaN(num)
          ? { ...item.value, [field]: null }
          : { ...item.value, [field]: num };

        const label =
          newValue.min !== null && newValue.max !== null
            ? `${newValue.min}-${newValue.max}`
            : null;

        return { ...item, label, value: newValue };
      });

      // Check if any non-empty row is invalid
      const hasInvalidRow = newDrafts.some(
        (d) =>
          (d.value.min !== null || d.value.max !== null) &&
          !isValidRange(d.value)
      );

      if (hasInvalidRow) {
        setDrafts(newDrafts as DraftDemographicDistribution[]);
      } else {
        onChange(
          newDrafts.filter((d) =>
            isValidRange(d.value)
          ) as DemographicDistribution<"age">[]
        );
      }
    },
    [drafts, onChange]
  );

  const getAgeError = useCallback(
    (dist: DraftDemographicDistribution) => {
      const value = dist.value;
      if (!value.min || !value.max) return null;
      if (value.min < MIN_AGE || value.min > MAX_AGE) {
        return i18n.t("dashboardNavigator.ageRange", {
          lowerBound: MIN_AGE,
          upperBound: MAX_AGE,
        });
      }
      if (value.max < MIN_AGE || value.max > MAX_AGE) {
        return i18n.t("dashboardNavigator.ageRange", {
          lowerBound: MIN_AGE,
          upperBound: MAX_AGE,
        });
      }
      if (value.max <= value.min) {
        return i18n.t("dashboardNavigator.maxSmallerThanMin");
      }
      return null;
    },
    [i18n]
  );

  return (
    <VStack className="space-y-4 pb-2">
      {drafts.slice(0, 1).map((dist, index) => {
        const value = dist.value;
        const error = getAgeError(dist);
        // const isEmptyRow = value.min === null && value.max === null;

        return (
          <VStack key={dist._key} className="space-y-3">
            <HStack className="items-start gap-3">
              <RangeSlider
                className="flex-1"
                disabled={disabled}
                max={MAX_AGE}
                min={MIN_AGE}
                onMaxChange={(v) => updateAgeRange(index, "max", v.toString())}
                onMinChange={(v) => updateAgeRange(index, "min", v.toString())}
                valueMax={value.max ?? MAX_AGE}
                valueMin={value.min ?? MIN_AGE}
              />
              <Text className="text-xs font-semibold text-typography-900 w-14 text-right mt-1">
                {value.min ?? MIN_AGE} – {value.max ?? MAX_AGE}
              </Text>
            </HStack>

            {/* Weight slider */}
            {/* {!isEmptyRow && (
              <HStack className="items-center space-x-4 pl-1">
                <Text className="text-xs text-typography-400 flex-shrink-0">
                  {i18n.t("dashboardNavigator.weight")}
                </Text>
                <Slider
                  className="flex-1"
                  isDisabled={disabled || !!error}
                  maxValue={100}
                  minValue={0}
                  onChange={(val: number) => {
                    updateWeight(index, val);
                  }}
                  step={1}
                  value={Math.round(dist.weight * 100)}
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
                <HStack className="items-center space-x-1 flex-shrink-0">
                  <Input
                    className="h-8 w-12 border border-outline-200 rounded-xl bg-background-50"
                    isDisabled={disabled || !!error}
                  >
                    <InputField
                      keyboardType="numeric"
                      onChangeText={(val: string) => {
                        const num = parseFloat(val);
                        if (!isNaN(num) && num >= 0 && num <= 100) {
                          updateWeight(index, num);
                        }
                      }}
                      value={Math.round(dist.weight * 100).toString()}
                    />
                  </Input>
                  <Text className="text-sm text-typography-600">%</Text>
                </HStack>
              </HStack>
            )} */}

            {error && <Text className="text-xs text-warning-600">{error}</Text>}
          </VStack>
        );
      })}
    </VStack>
  );
};

export default AgeRangeEditor;
